<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="step-2">
    <div class="details">
      <div class="date">
        {{ formattedData.dateInLocale }}
      </div>
      <div class="time">
        {{ formattedData.time }}
      </div>
      <div class="timezone">
        <i class="isif isif-globe" />{{ bookings.timezone }}
      </div>
    </div>
    <form
      class="form"
      @submit.prevent="submit"
    >
      <div class="fields">
        <div
          v-for="field in fields"
          :key="field.key"
          class="field"
        >
          <label :for="field.key">
            {{ field.label }}
            <em
              v-if="field.required"
              class="required"
              :title="$t('booking.requiredField')"
            >*</em>
          </label>
          <div class="field-wrap">
            <template v-if="field.type === 'timerange'">
              <div class="time-wrap">
                <div class="date">
                  {{ formattedData.date }}
                </div>
                <select
                  v-model="field.value.from"
                  class="input-inner"
                >
                  <option
                    v-for="option in startTimeOptions"
                    :key="'from-' + option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>
              <div class="label concat">
                ~
              </div>
              <div class="time-wrap">
                <div class="date">
                  {{ isEndTimeCrossDay ? formattedData.dateAfterMidnight : formattedData.date }}
                </div>
                <select
                  v-model="field.value.to"
                  class="input-inner"
                >
                  <option
                    v-for="option in endTimeOptions"
                    :key="'to-' + option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>
            </template>
            <textarea
              v-else-if="field.type === 'textarea'"
              :id="field.key"
              v-model="field.value"
              :name="field.key"
              :required="field.required"
              :title="field.required ? $t('booking.requiredField') : ''"
              v-bind="field.attrs || {}"
              rows="3"
            />
            <div
              v-else-if="field.type === 'multi-inputs'"
              class="multi-inputs"
            >
              <el-input
                :id="field.key"
                v-model.trim="currentEmailInput"
                :name="field.key"
                :type="field.type"
                :title="field.required ? $t('booking.requiredField') : ''"
                @keydown.enter.native.prevent="addInput(currentEmailInput, false)"
              >
                <el-button
                  slot="append"
                  icon="el-icon-plus"
                  @click="addInput(currentEmailInput, false)"
                />
              </el-input>
              <tag-list
                class="mt-2"
                :value="emails"
                @remove="updateTagList"
              />
            </div>
            <input
              v-else
              :id="field.key"
              v-model="field.value"
              :name="field.key"
              :type="field.type"
              :required="field.required"
              :title="field.required ? $t('booking.requiredField') : ''"
              v-bind="field.attrs || {}"
            >
          </div>
        </div>
      </div>
      <div class="footer">
        <div class="message">
          <template v-if="submitError">
            <span>{{ $t('booking.submitError') }}</span>
            <span v-html="submitError" />
          </template>
        </div>
        <button
          type="submit"
          class="submit"
          :disabled="isBusy"
        >
          <span v-if="isBusy">{{ $t('booking.pleaseWait') }}</span>
          <span v-else>{{ $t('booking.confirm') }}</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import tagList from './TagList.vue'
import bookingApi from '@/services/api/booking'
import { Message } from 'element-ui'
import { getLocalizedErrorMessage } from '@/utils/error'

const timeFormat = 'A hh:mm'

export default {
  name: 'BookingStep2',
  components: {
    tagList
  },
  props: {
    bookings: {
      type: Object,
      default: () => ({})
    },
    date: {
      type: String,
      default: ''
    }
  },
  data: self => ({
    fields: [
      {
        type: 'text',
        key: 'first_name',
        required: true,
        label: self.$t('name'),
        value: '',
        attrs: {
          maxlength: 50
        }
      },
      {
        type: 'multi-inputs',
        key: 'emails',
        required: true,
        label: self.$t('email'),
        value: []
      },
      {
        type: 'text',
        key: 'phone',
        required: true,
        label: self.$t('phone'),
        validate: value => {
          const regex = /^(\+{0,})(\d{0,})([(]{1}\d{1,3}[)]{0,}){0,}(\s?\d+|\+\d{2,3}\s{1}\d+|\d+){1}[\s|-]?\d+([\s|-]?\d+){1,2}(\s){0,}$/gm
          return regex.test(value)
        },
        value: '',
        attrs: {
          maxlength: 50
        }
      },
      {
        type: 'text',
        key: 'company',
        label: self.$t('company'),
        value: '',
        attrs: {
          maxlength: 50
        }
      },
      {
        type: 'timerange',
        key: 'bookingTime',
        required: true,
        label: self.$t('booking.timeRange'),
        value: {
          from: '',
          to: ''
        }
      },
      {
        type: 'textarea',
        key: 'message',
        label: self.$t('booking.fieldMessage'),
        value: '',
        attrs: {
          maxlength: 1000
        }
      }
    ],
    bookingTimeInterval: 15,
    bookingTimeRangeHour: 3,
    isBusy: false,
    submitError: false,
    emails: [],
    currentEmailInput: ''
  }),
  computed: {
    ...mapGetters([
      'currentBuilding'
    ]),
    ...mapGetters({
      user: 'auth/user'
    }),
    formattedData () {
      const moment = this.$moment(this.date)

      const dateInLocale = moment.format('LL') + ' ' + moment.format('dddd')

      const times = [
        moment,
        moment.clone().add(this.bookingTimeRangeHour, 'hour')
      ]

      return {
        date: moment.format('YYYY-MM-DD'),
        dateAfterMidnight: moment.add(1, 'day').format('YYYY-MM-DD'),
        dateInLocale,
        times,
        time: times.map(t => t.format(timeFormat)).join(' ~ ')
      }
    },
    bookingTimes () {
      const interval = this.bookingTimeInterval
      const hour = this.formattedData.times[0].format('A hh:00')
      const endTime = this.formattedData.times[1].format(timeFormat)
      const times = [endTime]
      let mins = this.bookingTimeRangeHour * 60
      while ((mins -= interval) >= 0) {
        const time = this.$moment(hour, timeFormat).add(mins, 'minutes').format(timeFormat)
        times.push(time)
      }
      return times.reverse()
    },
    startTimeOptions () {
      const times = this.bookingTimes
      const end = this.getFieldValue('bookingTime').to
      if (end) {
        const index = Math.min(4, times.indexOf(end)) // Maximum set to 4
        return times.slice(0, index)
      }
      return times.slice(0, 4) // Only keep first 4 options (an hour)
    },
    endTimeOptions () {
      const times = this.bookingTimes
      const start = this.getFieldValue('bookingTime').from
      if (start) {
        const index = times.indexOf(start)
        return times.slice(index + 1, times.length)
      }
      return times
    },
    isFieldInvalid () {
      return this.fields.some(field => {
        if (!field.required) {
          return false
        }
        const value = field.value
        if (typeof value === 'object') {
          const keys = Object.keys(value)
          return !keys.length || keys.some(k => !value[k].length)
        } else {
          return !value.length || (field.validate ? !field.validate(value) : false)
        }
      })
    },
    isEndTimeCrossDay () {
      const bookingTime = this.getFieldValue('bookingTime')
      const from = this.$moment(bookingTime.from, timeFormat)
      const to = this.$moment(bookingTime.to, timeFormat)
      return to.isBefore(from)
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    init () {
      const times = this.bookingTimes
      this.setFieldValue('bookingTime', {
        from: times[0],
        to: times[times.length - 1]
      })
      // auth/user is the user login
      if (this.user.email) {
        this.emails = [this.user.email]
      }
      const autofills = [{
        bookingField: 'first_name',
        userField: 'name'
      }, {
        bookingField: 'emails',
        userField: 'email'
      }, {
        bookingField: 'phone',
        userField: 'phone'
      }, {
        bookingField: 'company',
        userField: 'company'
      }]
      autofills.forEach(item => {
        if (item.bookingField === 'emails') {
          this.setFieldValue(item.bookingField, [this.user[item.userField]])
        } else {
          this.setFieldValue(item.bookingField, this.user[item.userField])
        }
      })
    },
    addInput (value, isAutoAdd) {
      const input = value.trim()
      const regex = /^.+@.+$/
      const result = regex.test(value)
      if (!input && !isAutoAdd) {
        return Message.warning(this.$t('booking.emailEmptyHint'))
      }
      if (input && !result) {
        return Message.warning(this.$t('emailFormatInvalid'))
      }
      if (this.emails.includes(input)) {
        return Message.warning(this.$t('duplicateEmail'))
      }
      if (input && result) {
        this.emails.push(this.currentEmailInput)
        this.setFieldValue('emails', this.emails)
      }
      this.currentEmailInput = ''
    },
    updateTagList (val) {
      this.emails = val
      this.setFieldValue('emails', this.emails)
    },
    async submit (event) {
      if (this.isBusy) return
      // automatically add email when submit
      this.addInput(this.currentEmailInput, true)

      if (!this.emails.length) {
        return Message.warning(this.$t('booking.emailEmptyHint'))
      } else if (this.currentEmailInput || this.isFieldInvalid) {
        return
      }
      this.submitError = false
      this.isBusy = true

      const bookingTime = this.getFieldValue('bookingTime')
      const date = this.$moment(this.date).format('YYYY-MM-DD') + ' '
      const format = 'YYYY-MM-DD A hh:mm'
      const payload = {
        buildingId: this.currentBuilding.objectId,
        firstName: this.getFieldValue('first_name'),
        // lastName: '', // Currently unused
        emails: this.getFieldValue('emails'),
        phone: this.getFieldValue('phone'),
        company: this.getFieldValue('company'),
        message: this.getFieldValue('message'),
        bookingTime: this.$moment.tz(date + bookingTime.from, format, this.bookings.timezone).locale('en').format('x'),
        bookingEndTime: this.$moment.tz((this.isEndTimeCrossDay ? this.formattedData.dateAfterMidnight + ' ' : date) + bookingTime.to, format, this.bookings.timezone).locale('en').format('x')
      }

      try {
        const response = await bookingApi.create(payload)

        if (response && response.createdAt) {
          this.$emit('next', {
            details: this.formattedData,
            name: payload.firstName,
            emails: payload.emails
          })
        } else {
          Message.warning(getLocalizedErrorMessage(response.code))
          this.isBusy = false
        }
      } catch (e) {
        this.submitError = this.$t('somethingWrong')
        this.isBusy = false
      }
    },
    getFieldValue (key) {
      const field = this.fields.find(field => field.key === key)
      return field ? field.value : ''
    },
    setFieldValue (key, value) {
      const field = this.fields.find(field => field.key === key)
      if (!field) {
        return
      }
      field.value = value
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~css/addon/main';

.step-2 {
  display: flex
  flex-direction: column
}
.details {
  color: #4472CB
  font-weight: bold
  text-align: center
  line-height: 24px

  .timezone {
    margin-top: 10px

    i {
      margin-right: 5px
    }
  }
}
.form {
  flex: 1
  display: flex
  flex-direction: column
}
.fields {
  flex: 1
  display: flex
  flex-direction: column
  justify-content: center
  margin: 20px 0

  .field {
    margin-bottom: 15px
    display: flex
    flex-wrap: wrap
    justify-content: center
    align-items: flex-start
    line-height: 32px
  }
  label {
    width: 140px
    font-weight: bold
    flex-basis: 100%

    @media (min-width: $response) {
      flex-basis: auto
    }
    .required {
      color: #DE2222
      font-style: normal
    }
  }
  .field-wrap {
    width: 100%
    display: flex
    align-items: flex-end

    @media (min-width: $response) {
      width: 400px
    }
  }
  .time-wrap {
    .date {
      font-size: 12px
      line-height: 1
      margin-bottom: 3px
      color: $gray
    }
  }
  input, textarea, select {
    width: 100%
    line-height: 20px
    padding: 8px 10px
    font-size: 15px
    border: 1px solid #aaa
    border-radius: 5px
  }
  input, select {
    height: 32px
  }
  select {
    width: auto
    padding-top: 6px
    padding-bottom: 6px
  }
  .label {
    display: inline-block

    &.concat {
      margin: 0 10px
    }
  }
}
.multi-inputs {
  width: 100%

  .el-form-item {
    margin-bottom: 0
  }

  >>> .el-input {
    margin-bottom: 12px

    .el-input-group__append {
      height: 32px !important
      border: 1px solid $pink-color
      color: white
      background-color: $pink-color
      button {
        padding-top: 8px
        padding-bottom: 8px
      }
      &:hover {
        border: 1px solid $light-pink-color
        background-color: $light-pink-color
      }
    }
    &__inner {
      position: relative
      height: 32px
      border-radius: 5px 0 0 5px
      border-color: #aaa
      font-size: 16px

      &:focus {
        box-shadow: none
        border-color: $pink
      }
      &:hover {
        z-index: 2
      }
    }
  }
}
.footer {
  display: flex
  justify-content: center
  align-items: center
  flex-direction: column

  .message {
    font-size: 15px
    height: 15px
    margin-bottom: 8px

    span {
      color: $red
      text-shadow: none
    }
  }
  .submit {
    min-width: 160px
    padding-left: 15px
    padding-right: 15px
    line-height: 40px
    background: $pink-color
    color: #FFF
    font-size: 15px
    border-radius: 5px
    &:hover {
      background-color: $light-pink-color
    }

    &[disabled] {
      background: #C4C4C4
      cursor: default
    }
    span {
      text-shadow: none
    }
  }
}
</style>
